/**
  * @class mmodAppLogo
  * @description
  * Application Logo is a small module used in mPatrol for mobile phones.
  * It just displays Mosy logo which can be pressed to display Application Wall for switching modules.
  * Logo also can also indicate connection to server and peering and GPS location.
  * Module is implemented without workflow, as a pure module named mmodAppLogo.
  * Module listens on various messages to update its appearance accordingly:
  * - mmUsersStatus - used to update operational status of user
  * - mmServerConnected/mmServerDisconnected - to update server connection icon
  * - mmPeeringConnected/mmPeeringDisconnected - to update peering connection icon
  * - mmGps - to update GPS icon
  */

import mmodModule from 'mmodModule'

export default class mmodAppLogo extends mmodModule {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([/* put module specific dependencies here */], dependencies)
    cfg_schema = _.extend({
      init: {
        type: 'object',
        default: { type: 'float', x: -64, y: 0, width: 64, height: 64, order: 9 },
        description: 'Initial window setup; same structure as required by `mmWindowSet`',
      },
      icon: {
        type: 'string',
        default: 'mosy_m',
        description: 'Changes the default icon for navigation',
      },
    }, cfg_schema)
    if(require('msysFS').device()!='phone') cfg.disabled = true
    super(id, cfg, cfg_schema, dependencies)
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * initialization of GUI components
  */
  initWindow() {
    this.loadForm(require('./mmodAppLogo.json'))
    this.subscribe('#GPS')

    // check if we have gps signal
    this.gpsTimer = 10
    this._oldGps = 0
    const elIcon = this.el.find('mele-button@applogo')
    if(elIcon) elIcon.iconleft = this.config.icon
    setInterval(()=>{
      this.checkGpsSignal()
    }, this.gpsTimer*1000)

    this.mmStart()
  } // initWindow

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysDomainClient informs all listeners that connection to mServer is established
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - app_id of the server
  * @param {string} params.url - URL of connected server
  * @param {string} [params.ident] - Identity string of server
  * @param {string} [params.client_ip] - IP of the client as recognized by the server
  */
  mmServerConnected(sender, params) {
    this.el.find('@online').value = 1
  } // mmServerConnected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysDomainClient informs all listeners that connection to mServer has dropped
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.reason] - reason for close
  * @param {string} params.url - URL of disconnected server
  */
  mmServerDisconnected(sender, params) {
    this.el.find('@online').value = 0
  } // mmServerDisconnected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysPeeringClient informs all listeners that connection to peering is established
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - unique application ID
  * @param {string} [params.url] - URL of connected peering
  * @param {string} [params.ident] - Identity string of server
  * @param {string} [params.client_ip] - IP of the client as recognized by the peering
  */
  mmPeeringConnected(sender, params) {
    this.el.find('@peering').value = 1
  } // mmPeeringConnected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysPeeringClient informs all listeners that connection to peering has dropped
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.reason] - reason for close
  * @param {string} [params.url] - URL of disconnected peering
  */
  mmPeeringDisconnected(sender, params) {
    this.el.find('@peering').value = 0
  } // mmPeeringDisconnected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * information on change of location
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.dev] - ID of device producing the GPS info
  * @param {string} params.gps - geohash of GPS position
  * @param {number} [params.speed] - speed in km/h
  * @param {number} [params.heading] - movement heading in degrees
  * @param {integer} [params.satellites] - number of satellites
  * @param {integer} [params.gps_dt] - GPS reported time in microseconds
  * @param {integer} [params.fix] - true if exists on sattelite
  */
  mmGps(sender, params) {
    if(params.gps) { this.last_gps_update = Date.now() }
  } // mmGps

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Checks if the mmGps is still sending valid data
  */
  checkGpsSignal() {
    this.gpsvalue = (this.last_gps_update > Date.now()-this.gpsTimer*1000) ? 1 : 0
    if(this.gpsvalue != this._oldGps) {
      this._oldGps = this.el.find('@gps').value = this.gpsvalue
    }
  } // checkGpsSignal

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * notification on changed user status
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - unique application ID
  * @param {string} params.status - new status
  * @param {string} [params.info] - additional info about status change
  */
  mmUserStatus(sender, params) {
    super.mmUserStatus(sender, params)
    this.status = params.status
    this.el.find('.mele-button@applogo').color =
    /^g/.test(this.status) ? 'green' :
      /^o/.test(this.status) ? 'orange' :
        /^r/.test(this.status) ? 'red' :
          'gray'
  } // mmUserStatus

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by &lt;mele-button> to parent module when a button is clicked
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.holder] - key attribute of button's parent container
  * @param {string} params.button - key attribute of the button
  * @param {integer} [params.count] - number of times button was clicked
  * @param {boolean} [params.long] - shows if press was long or not
  */
  mmButtonClicked(sender, params) {
    if(params.button=='applogo') {
      this.publish('mmodAppWall', 'mmOpen')
    }
  } // mmButtonClicked
} // mmodAppLogo
